/**
 * SlimFormQueue Component (Unified Quality Check)
 *
 * This component displays a unified list of SlimForm submissions AND
 * Data Collection records with filtering, allowing users to review both.
 */

import { DFPEmpty } from "@/components/empty/dfp-empty";
import { DFPTable } from "@/components/tables/dfp-table";
import type {
	BaseTableAction,
	BaseTableState,
} from "@/components/tables/shared-table-state";
import { baseInitialState } from "@/components/tables/shared-table-state";
import {
	type SlimFormStatus,
	type SlimFormSubmission,
	batchAutoApproveSubmissions,
	getSlimFormSubmissionCounts,
	getSlimFormSubmissions,
} from "@/services/slim-form-service";
import {
	getRecords,
	type CollectionRecord,
} from "@/services/data-collection-service";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import {
	CheckCircleOutlined,
	ClockCircleOutlined,
	CloseCircleOutlined,
	DownloadOutlined,
	EyeOutlined,
	PlusOutlined,
	ReloadOutlined,
	SyncOutlined,
	ThunderboltOutlined,
	WarningOutlined,
	CameraOutlined,
	FileSearchOutlined,
} from "@ant-design/icons";
import {
	Button,
	Card,
	Col,
	Divider,
	Input,
	Row,
	Select,
	Space,
	Statistic,
	Tag,
	message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import React, {
	useState,
	useEffect,
	useReducer,
	useCallback,
	useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import "./SlimFormQueue.scss";

const { Search } = Input;

/* ---------------------------------------------------------------------
   Unified Row Type
   --------------------------------------------------------------------- */
type CaptureType = "asset" | "field_data";

interface UnifiedQCRow {
	/** Unique key for the table (prefixed to avoid collisions) */
	rowKey: string;
	captureType: CaptureType;
	id: number;
	status: string;
	locationName: string;
	submittedBy: string;
	createdAt: string;
	/** Original record for type-specific actions */
	_slimForm?: SlimFormSubmission;
	_dcRecord?: CollectionRecord;
}

/* ---------------------------------------------------------------------
   Types
   --------------------------------------------------------------------- */
export type SlimFormTableAction =
	| BaseTableAction
	| { type: "SET_SUBMISSIONS"; payload: UnifiedQCRow[] }
	| { type: "SET_COUNTS"; payload: Record<SlimFormStatus, number> }
	| { type: "SET_SELECTED_STATUS"; payload: SlimFormStatus | "ALL" }
	| { type: "SET_FILTERED_SUBMISSIONS"; payload: UnifiedQCRow[] }
	| { type: "SET_SELECTED_TYPE"; payload: CaptureType | "ALL" };

export interface SlimFormTableState extends BaseTableState {
	submissions: UnifiedQCRow[];
	filteredSubmissions: UnifiedQCRow[];
	counts: Record<SlimFormStatus, number>;
	selectedStatus: SlimFormStatus | "ALL";
	selectedType: CaptureType | "ALL";
}

/* ---------------------------------------------------------------------
   Session Storage Keys
   --------------------------------------------------------------------- */
const SESSION_STORAGE_KEY = "slimFormQueue_selectedStatus";

/* ---------------------------------------------------------------------
   Initial State + Reducer
   --------------------------------------------------------------------- */
const getInitialSelectedStatus = (): SlimFormStatus | "ALL" => {
	try {
		const saved = sessionStorage.getItem(SESSION_STORAGE_KEY);
		if (saved) {
			return saved as SlimFormStatus | "ALL";
		}
	} catch (error) {
		console.error(
			"[SlimFormQueue] Error loading filter from session storage:",
			error,
		);
	}
	return "ALL";
};

const initialState: SlimFormTableState = {
	...baseInitialState,
	submissions: [],
	filteredSubmissions: [],
	counts: {} as Record<SlimFormStatus, number>,
	selectedStatus: "ALL", // Will be restored from storage on mount
	selectedType: "ALL",
	sortBy: "createdAt",
	sortOrder: "desc",
	pageSize: 20,
};

function reducer(
	state: SlimFormTableState,
	action: SlimFormTableAction,
): SlimFormTableState {
	switch (action.type) {
		case "SET_SUBMISSIONS":
			return { ...state, submissions: action.payload };
		case "SET_COUNTS":
			return { ...state, counts: action.payload };
		case "SET_SELECTED_STATUS":
			return { ...state, selectedStatus: action.payload, pageNum: 1 };
		case "SET_SELECTED_TYPE":
			return { ...state, selectedType: action.payload, pageNum: 1 };
		case "SET_FILTERED_SUBMISSIONS":
			return {
				...state,
				filteredSubmissions: action.payload,
				count: action.payload.length,
			};
		case "SET_SEARCH_VAL":
			return { ...state, searchVal: action.payload, pageNum: 1 };
		case "SET_PAGE_NUM":
			return { ...state, pageNum: action.payload };
		case "SET_PAGE_SIZE":
			return { ...state, pageSize: action.payload, pageNum: 1 };
		case "SET_IS_LOADING":
			return { ...state, isLoading: action.payload };
		case "SET_SORT_BY":
			return { ...state, sortBy: action.payload };
		case "SET_SORT_ORDER":
			return { ...state, sortOrder: action.payload };
		default:
			return state;
	}
}

export const SlimFormQueue: React.FC = () => {
	const navigate = useNavigate();
	const [state, dispatch] = useReducer(reducer, initialState);
	const [isInitialLoad, setIsInitialLoad] = useState(true);
	const [isBatchProcessing, setIsBatchProcessing] = useState(false);
	const hasDataCollection = useFeatureFlag("data_collection");

	/**
	 * Restore filter from session storage on mount
	 */
	useEffect(() => {
		if (isInitialLoad) {
			const savedStatus = getInitialSelectedStatus();
			if (savedStatus !== state.selectedStatus) {
				dispatch({ type: "SET_SELECTED_STATUS", payload: savedStatus });
			}
			setIsInitialLoad(false);
		}
	}, [isInitialLoad, state.selectedStatus]);

	/**
	 * Normalize a DC status to a SlimForm-compatible status for the status filter
	 */
	const normalizeDCStatus = (status: string): SlimFormStatus => {
		const map: Record<string, SlimFormStatus> = {
			PENDING: "PENDING",
			PROCESSING: "PROCESSING",
			COMPLETED: "PROCESSED",
			REVIEWED: "APPROVED",
			FLAGGED: "REJECTED",
		};
		return map[status] || "PENDING";
	};

	/**
	 * Loads submissions and counts (SlimForm + DC records)
	 */
	const loadData = useCallback(async () => {
		dispatch({ type: "SET_IS_LOADING", payload: true });
		try {
			// Fetch SlimForm + counts in parallel, and optionally DC records
			const fetches: [
				Promise<SlimFormSubmission[]>,
				Promise<Record<SlimFormStatus, number>>,
				Promise<{ records: CollectionRecord[]; total: number } | null>,
			] = [
				getSlimFormSubmissions(),
				getSlimFormSubmissionCounts(),
				hasDataCollection
					? getRecords({ limit: 500 })
					: Promise.resolve(null),
			];

			const [submissionsData, countsData, dcResult] = await Promise.all(fetches);

			// Normalize SlimForm rows
			const sfRows: UnifiedQCRow[] = submissionsData.map((s) => ({
				rowKey: `sf-${s.id}`,
				captureType: "asset" as CaptureType,
				id: s.id,
				status: s.status,
				locationName:
					s.locationAddress?.split(",")[0]?.trim() ||
					s.locationName ||
					"-",
				submittedBy: s.userName || "-",
				createdAt: s.createdAt,
				_slimForm: s,
			}));

			// Normalize DC rows
			let dcRows: UnifiedQCRow[] = [];
			if (dcResult?.records) {
				dcRows = dcResult.records.map((r) => ({
					rowKey: `dc-${r.id}`,
					captureType: "field_data" as CaptureType,
					id: r.id,
					status: normalizeDCStatus(r.status),
					locationName: r.locationName || "-",
					submittedBy: r.collectedByName || "-",
					createdAt: r.createdAt,
					_dcRecord: r,
				}));

				// Merge DC counts into the counts object
				for (const r of dcResult.records) {
					const mapped = normalizeDCStatus(r.status);
					countsData[mapped] = (countsData[mapped] || 0) + 1;
				}
			}

			const all = [...sfRows, ...dcRows].sort(
				(a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
			);

			dispatch({ type: "SET_SUBMISSIONS", payload: all });
			dispatch({ type: "SET_COUNTS", payload: countsData });
		} catch (error) {
			console.error("Error loading data:", error);
			message.error(
				error instanceof Error ? error.message : "Failed to load submissions",
			);
		} finally {
			dispatch({ type: "SET_IS_LOADING", payload: false });
		}
	}, [hasDataCollection]);

	/**
	 * Handles batch auto-approval of all eligible PROCESSED submissions
	 */
	const handleBatchAutoApprove = useCallback(async () => {
		// Count how many PROCESSED submissions exist
		const processedCount = state.counts.PROCESSED || 0;

		if (processedCount === 0) {
			message.info("No submissions ready for review");
			return;
		}

		// Show confirmation
		const confirmed = window.confirm(
			`This will process ALL ${processedCount} "Ready for Review" submission${processedCount !== 1 ? "s" : ""} for auto-approval eligibility.\n\n` +
				"Process includes:\n" +
				"• Fix status mismatches (submissions with assets but wrong status)\n" +
				"• Auto-approve eligible submissions\n" +
				"• Reject submissions with duplicate asset tags\n\n" +
				`This may take a few minutes for ${processedCount} submissions.\n\n` +
				"Continue?",
		);

		if (!confirmed) {
			return;
		}

		setIsBatchProcessing(true);
		const hide = message.loading(
			`Processing ${processedCount} submissions... This may take a few minutes.`,
			0,
		);

		try {
			// Pass undefined to process all (no limit)
			const results = await batchAutoApproveSubmissions(undefined);

			hide();

			// Show detailed results
			const notEligibleCount = results.total - results.eligible;

			let resultMessage = "Batch auto-approval completed!\n\n";

			if (results.statusFixed > 0) {
				resultMessage += `🔧 ${results.statusFixed} status${results.statusFixed !== 1 ? "es" : ""} fixed (already had assets)\n`;
			}
			if (results.approved > 0) {
				resultMessage += `✓ ${results.approved} approved and created as assets\n`;
			}
			if (results.rejected > 0) {
				resultMessage += `${results.rejected} rejected (duplicate asset tags)\n`;
			}
			if (results.failed > 0) {
				resultMessage += `✗ ${results.failed} failed\n`;
			}
			if (notEligibleCount > 0) {
				resultMessage += `⊘ ${notEligibleCount} not eligible (insufficient confidence or missing fields)\n`;
			}

			if (results.approved > 0) {
				message.success(resultMessage, 10);
			} else if (results.rejected > 0 || results.failed > 0) {
				message.warning(resultMessage, 8);
			} else {
				message.info(resultMessage, 8);
			}

			// Reload data to reflect changes
			await loadData();
		} catch (error) {
			hide();
			console.error("Error during batch auto-approval:", error);
			message.error(
				error instanceof Error
					? error.message
					: "Failed to run batch auto-approval",
			);
		} finally {
			setIsBatchProcessing(false);
		}
	}, [state.counts.PROCESSED, loadData]);

	/**
	 * Export queue data to Excel
	 */
	const handleExportCSV = useCallback(() => {
		try {
			const dataToExport = state.filteredSubmissions;

			if (dataToExport.length === 0) {
				message.warning("No data to export");
				return;
			}

			const headers = [
				"ID",
				"Type",
				"Status",
				"Location",
				"Submitted By",
				"Created",
			];

			const rows = dataToExport.map((row) => [
				row.id,
				row.captureType === "asset" ? "Asset Capture" : "Field Data",
				row.status,
				row.locationName,
				row.submittedBy,
				new Date(row.createdAt).toLocaleString(),
			]);

			const worksheetData = [headers, ...rows];
			const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
			worksheet["!cols"] = [
				{ wch: 8 },
				{ wch: 14 },
				{ wch: 14 },
				{ wch: 30 },
				{ wch: 20 },
				{ wch: 20 },
			];

			const workbook = XLSX.utils.book_new();
			XLSX.utils.book_append_sheet(workbook, worksheet, "Quality Check");

			const statusFilter =
				state.selectedStatus === "ALL"
					? "all"
					: state.selectedStatus.toLowerCase();
			const timestamp = new Date().toISOString().split("T")[0];
			const filename = `quality-check-${statusFilter}-${timestamp}.xlsx`;
			XLSX.writeFile(workbook, filename);

			message.success(`Exported ${dataToExport.length} items to Excel`);
		} catch (error) {
			console.error("Error exporting Excel:", error);
			message.error("Failed to export Excel file");
		}
	}, [state.filteredSubmissions, state.selectedStatus]);

	/**
	 * Initial data load
	 */
	useEffect(() => {
		loadData();
	}, [loadData]);

	/**
	 * Save selected status to session storage whenever it changes
	 */
	useEffect(() => {
		// Skip saving on the very first render before restoration
		if (!isInitialLoad) {
			try {
				sessionStorage.setItem(SESSION_STORAGE_KEY, state.selectedStatus);
			} catch (error) {
				console.error(
					"[SlimFormQueue] Error saving filter to session storage:",
					error,
				);
			}
		}
	}, [state.selectedStatus, isInitialLoad]);

	/**
	 * Filters submissions when status, type, or search changes
	 */
	useEffect(() => {
		let filtered = [...state.submissions];

		// Filter by capture type
		if (state.selectedType !== "ALL") {
			filtered = filtered.filter((s) => s.captureType === state.selectedType);
		}

		// Filter by status
		if (state.selectedStatus !== "ALL") {
			filtered = filtered.filter((s) => s.status === state.selectedStatus);
		}

		// Filter by search text
		if (state.searchVal.trim()) {
			const search = state.searchVal.toLowerCase();
			filtered = filtered.filter(
				(s) =>
					s.locationName?.toLowerCase().includes(search) ||
					s.submittedBy?.toLowerCase().includes(search) ||
					s.id.toString().includes(search) ||
					// Search SlimForm-specific fields
					s._slimForm?.locationAddress?.toLowerCase().includes(search) ||
					s._slimForm?.assetTag?.toLowerCase().includes(search) ||
					// Search DC-specific fields
					s._dcRecord?.templateName?.toLowerCase().includes(search) ||
					s._dcRecord?.areaName?.toLowerCase().includes(search),
			);
		}

		dispatch({ type: "SET_FILTERED_SUBMISSIONS", payload: filtered });
	}, [state.submissions, state.selectedStatus, state.selectedType, state.searchVal]);

	/**
	 * Gets status tag configuration
	 */
	const getStatusTag = (status: SlimFormStatus) => {
		const statusConfig: Record<
			SlimFormStatus,
			{ color: string; icon: React.ReactNode; text: string }
		> = {
			PENDING: {
				color: "default",
				icon: <ClockCircleOutlined />,
				text: "Pending",
			},
			PROCESSING: {
				color: "processing",
				icon: <SyncOutlined spin />,
				text: "Processing",
			},
			PROCESSED: {
				color: "success",
				icon: <CheckCircleOutlined />,
				text: "Ready for Review",
			},
			APPROVED: {
				color: "green",
				icon: <CheckCircleOutlined />,
				text: "Approved",
			},
			REJECTED: {
				color: "red",
				icon: <CloseCircleOutlined />,
				text: "Rejected",
			},
			FAILED: {
				color: "error",
				icon: <WarningOutlined />,
				text: "Failed",
			},
		};

		const config = statusConfig[status];
		return (
			<Tag color={config.color} icon={config.icon}>
				{config.text}
			</Tag>
		);
	};

	/**
	 * Handle row click -- navigate to the correct review page based on type
	 */
	const handleRowClick = useCallback(
		(record: UnifiedQCRow) => {
			if (record.captureType === "field_data") {
				navigate(`/data-collection/${record.id}`);
			} else {
				navigate(`/slim-form/review/${record.id}`);
			}
		},
		[navigate],
	);

	/**
	 * Render a capture type badge
	 */
	const renderTypeTag = (type: CaptureType) => {
		if (type === "asset") {
			return (
				<Tag icon={<CameraOutlined />} color="purple">
					Asset
				</Tag>
			);
		}
		return (
			<Tag icon={<FileSearchOutlined />} color="cyan">
				Field Data
			</Tag>
		);
	};

	/**
	 * Table columns
	 */
	const columns: ColumnsType<UnifiedQCRow> = [
		{
			title: "ID",
			dataIndex: "id",
			key: "id",
			width: 80,
			sorter: (a, b) => a.id - b.id,
		},
		{
			title: "Type",
			dataIndex: "captureType",
			key: "captureType",
			width: 130,
			render: (type: CaptureType) => renderTypeTag(type),
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			width: 150,
			render: (status: SlimFormStatus) => getStatusTag(status),
		},
		{
			title: "Location",
			dataIndex: "locationName",
			key: "location",
			width: 220,
			ellipsis: true,
		},
		{
			title: "Submitted By",
			dataIndex: "submittedBy",
			key: "submittedBy",
			ellipsis: true,
			width: 200,
		},
		{
			title: "Created",
			dataIndex: "createdAt",
			key: "createdAt",
			width: 200,
			render: (date: string) => new Date(date).toLocaleString(),
			sorter: (a, b) =>
				new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
			defaultSortOrder: "descend",
		},
		{
			title: "Actions",
			key: "actions",
			width: 120,
			fixed: "right",
			render: (_, record) => (
				<Space>
					<Button
						type="primary"
						size="small"
						icon={<EyeOutlined />}
						onClick={(e) => {
							e.stopPropagation();
							handleRowClick(record);
						}}
					>
						View
					</Button>
				</Space>
			),
		},
	];

	// Memoize paginated data
	const paginatedData = useMemo(() => {
		const start = (state.pageNum - 1) * state.pageSize;
		const end = start + state.pageSize;
		return state.filteredSubmissions.slice(start, end);
	}, [state.filteredSubmissions, state.pageNum, state.pageSize]);

	// Create table props
	const tableProps = {
		dataSource: paginatedData as any,
		columns: columns as any,
		loading: state.isLoading,
		rowKey: "rowKey",
		scroll: { x: 1200 },
		onRow: (record: any) => ({
			onClick: () => handleRowClick(record),
			style: { cursor: "pointer" },
		}),
		pagination: {
			current: state.pageNum,
			pageSize: state.pageSize,
			total: state.count,
			showTotal: (total: number, range: [number, number]) => (
				<span>{`Showing ${range[0].toLocaleString()} - ${range[1].toLocaleString()} of ${total.toLocaleString()} items`}</span>
			),
			pageSizeOptions: ["10", "20", "50", "100"],
			showSizeChanger: true,
			onChange: (page: number) => {
				dispatch({ type: "SET_PAGE_NUM", payload: page });
			},
			onShowSizeChange: (_current: number, size: number) => {
				dispatch({ type: "SET_PAGE_SIZE", payload: size });
				dispatch({ type: "SET_PAGE_NUM", payload: 1 });
			},
		},
	};

	return (
		<div className="slim-form-queue-container">
			<Card
				title="Quality Check"
				extra={
					<Space wrap>
						<Button
						type="primary"
						icon={<PlusOutlined />}
						onClick={() =>
							navigate("/slim-form/capture", {
								state: { from: "/admin/quality-check" },
							})
						}
					>
						New Submission
					</Button>
						<Button
							icon={<ThunderboltOutlined />}
							onClick={handleBatchAutoApprove}
							loading={isBatchProcessing}
							disabled={
								state.isLoading || isBatchProcessing || !state.counts.PROCESSED
							}
							title={
								isBatchProcessing
									? "Batch processing in progress..."
									: !state.counts.PROCESSED
										? "No submissions ready for review"
										: "Fix statuses + auto-approve eligible submissions (rejects duplicates)"
							}
						>
							Auto-Approve Eligible
						</Button>
						<Button
							icon={<ReloadOutlined />}
							onClick={loadData}
							loading={state.isLoading}
						>
							Refresh
						</Button>
						<Button
							icon={<DownloadOutlined />}
							onClick={handleExportCSV}
							disabled={
								state.isLoading || state.filteredSubmissions.length === 0
							}
						>
							Download Excel
						</Button>
					</Space>
				}
				className="queue-card"
			>
				{/* Statistics */}
				<Row
					gutter={[24, 16]}
					className="statistics-row"
					style={{ display: "flex", justifyContent: "space-between" }}
				>
					<Col
						xs={12}
						sm={8}
						md={6}
						lg={6}
						xl={3}
						xxl={3}
						flex="1"
						style={{ minWidth: "140px" }}
					>
						<Statistic
							title="Total"
							value={state.submissions.length}
							valueStyle={{ color: "#1890ff" }}
						/>
					</Col>
					<Col
						xs={12}
						sm={8}
						md={6}
						lg={6}
						xl={3}
						xxl={3}
						flex="1"
						style={{ minWidth: "140px" }}
					>
						<Statistic
							title="Pending"
							value={state.counts.PENDING || 0}
							valueStyle={{ color: "#8c8c8c" }}
							prefix={<ClockCircleOutlined />}
						/>
					</Col>
					<Col
						xs={12}
						sm={8}
						md={6}
						lg={6}
						xl={3}
						xxl={3}
						flex="1"
						style={{ minWidth: "140px" }}
					>
						<Statistic
							title="Processing"
							value={state.counts.PROCESSING || 0}
							valueStyle={{ color: "#1890ff" }}
							prefix={<SyncOutlined spin={state.counts.PROCESSING > 0} />}
						/>
					</Col>
					<Col
						xs={12}
						sm={8}
						md={6}
						lg={6}
						xl={3}
						xxl={3}
						flex="1"
						style={{ minWidth: "140px" }}
					>
						<Statistic
							title="Ready"
							value={state.counts.PROCESSED || 0}
							valueStyle={{ color: "#52c41a" }}
							prefix={<CheckCircleOutlined />}
						/>
					</Col>
					<Col
						xs={12}
						sm={8}
						md={6}
						lg={6}
						xl={3}
						xxl={3}
						flex="1"
						style={{ minWidth: "140px" }}
					>
						<Statistic
							title="Approved"
							value={state.counts.APPROVED || 0}
							valueStyle={{ color: "#52c41a" }}
							prefix={<CheckCircleOutlined />}
						/>
					</Col>
					<Col
						xs={12}
						sm={8}
						md={6}
						lg={6}
						xl={3}
						xxl={3}
						flex="1"
						style={{ minWidth: "140px" }}
					>
						<Statistic
							title="Rejected"
							value={state.counts.REJECTED || 0}
							valueStyle={{ color: "#ff4d4f" }}
							prefix={<CloseCircleOutlined />}
						/>
					</Col>
					<Col
						xs={12}
						sm={8}
						md={6}
						lg={6}
						xl={3}
						xxl={3}
						flex="1"
						style={{ minWidth: "140px" }}
					>
						<Statistic
							title="Failed"
							value={state.counts.FAILED || 0}
							valueStyle={{ color: "#ff4d4f" }}
							prefix={<WarningOutlined />}
						/>
					</Col>
				</Row>

				{/* Filters */}
				<div className="filters-section">
					<Space size="middle" wrap>
						{hasDataCollection && (
							<Select
								style={{ width: 180 }}
								value={state.selectedType}
								onChange={(value) =>
									dispatch({ type: "SET_SELECTED_TYPE", payload: value })
								}
								options={[
									{ label: "All Types", value: "ALL" },
									{ label: "Asset Captures", value: "asset" },
									{ label: "Field Data", value: "field_data" },
								]}
							/>
						)}
						<Select
							style={{ width: 200 }}
							value={state.selectedStatus}
							onChange={(value) =>
								dispatch({ type: "SET_SELECTED_STATUS", payload: value })
							}
							options={[
								{ label: "All Statuses", value: "ALL" },
								{ label: "Pending", value: "PENDING" },
								{ label: "Processing", value: "PROCESSING" },
								{ label: "Ready for Review", value: "PROCESSED" },
								{ label: "Approved", value: "APPROVED" },
								{ label: "Rejected", value: "REJECTED" },
								{ label: "Failed", value: "FAILED" },
							]}
						/>
						<Search
							placeholder="Search by ID, location, user..."
							style={{ width: 350 }}
							value={state.searchVal}
							onChange={(e) =>
								dispatch({ type: "SET_SEARCH_VAL", payload: e.target.value })
							}
							allowClear
						/>
					</Space>
				</div>

				<Divider />

				{/* Table */}
				<DFPTable
					{...tableProps}
					locale={{
						emptyText:
							!state.isLoading && state.filteredSubmissions.length === 0 ? (
								<DFPEmpty
									className="w-100 h-100"
									style={{ minHeight: "50dvh" }}
									description="No submissions found. Try adjusting your filters or search."
								/>
							) : null,
					}}
				/>
			</Card>
		</div>
	);
};
